import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { darkTheme, lightTheme } from '../theme/createKuramaTheme';
import type { ThemeMode } from '../theme/createPaletteBridge';
import type { AppProviderProps, ThemeModeContextValue } from './types';
import { ThemeModeContext } from './useThemeMode';
import { DEFAULT_THEME_PREFERENCE, DEFAULT_THEME_PREFERENCE_STORAGE_KEY, resolveThemeMode, resolveThemePreference, writeThemePreference, type ThemePreference } from './themePreference';
import { themePreferenceStore } from './themePreferenceStore';
const noopSetMode = (_mode: ThemeMode): void => {};
const noopToggleMode = (): void => {};
const noopSetPreference = (_preference: ThemePreference): void => {};
const ThemeProvider = t0 => {
  const $ = _c(4);
  const {
    children,
    theme
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CssBaseline enableColorScheme={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== theme) {
    t2 = <MUIThemeProvider theme={theme}>{t1}{children}</MUIThemeProvider>;
    $[1] = children;
    $[2] = theme;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const subscribeToThemePreference = (listener: () => void): (() => void) => themePreferenceStore.subscribe(listener);
const AppProvider = t0 => {
  const $ = _c(36);
  const {
    children,
    theme: themeOverride,
    mode: controlledMode,
    isThemeSwitchingEnabled: t1,
    themePreferenceStorageKey: t2,
    defaultThemePreference: t3
  } = t0;
  const isThemeSwitchingEnabled = t1 === undefined ? false : t1;
  const themePreferenceStorageKey = t2 === undefined ? DEFAULT_THEME_PREFERENCE_STORAGE_KEY : t2;
  const defaultThemePreference = t3 === undefined ? DEFAULT_THEME_PREFERENCE : t3;
  const parentThemeMode = useContext(ThemeModeContext);
  const isOutermostProvider = parentThemeMode == null;
  const isThemePinned = themeOverride != null;
  const isModeControlled = controlledMode !== undefined;
  const isSwitchingDisabled = !isThemeSwitchingEnabled || isThemePinned || isModeControlled;
  let t4;
  if ($[0] !== defaultThemePreference || $[1] !== isThemeSwitchingEnabled || $[2] !== themePreferenceStorageKey) {
    t4 = () => resolveThemePreference(themePreferenceStorageKey, isThemeSwitchingEnabled, defaultThemePreference);
    $[0] = defaultThemePreference;
    $[1] = isThemeSwitchingEnabled;
    $[2] = themePreferenceStorageKey;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const readEffectivePreference = t4;
  let t5;
  if ($[4] !== readEffectivePreference) {
    t5 = () => resolveThemeMode(readEffectivePreference());
    $[4] = readEffectivePreference;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const readResolvedMode = t5;
  const preference = useSyncExternalStore(subscribeToThemePreference, readEffectivePreference, _temp);
  const resolvedModeFromPreference = useSyncExternalStore(subscribeToThemePreference, readResolvedMode, _temp2);
  let t6;
  bb0: {
    if (controlledMode !== undefined) {
      t6 = controlledMode;
      break bb0;
    }
    if (isThemePinned) {
      t6 = themeOverride.mode;
      break bb0;
    }
    t6 = resolvedModeFromPreference;
  }
  const resolvedMode = t6;
  const activeTheme = themeOverride ?? (resolvedMode === "dark" ? darkTheme : lightTheme);
  let t7;
  if ($[6] !== isSwitchingDisabled || $[7] !== themePreferenceStorageKey) {
    t7 = next => {
      if (isSwitchingDisabled) {
        return;
      }
      writeThemePreference(themePreferenceStorageKey, next);
      themePreferenceStore.notify();
    };
    $[6] = isSwitchingDisabled;
    $[7] = themePreferenceStorageKey;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const setPreference = t7;
  let t8;
  if ($[9] !== isSwitchingDisabled || $[10] !== setPreference) {
    t8 = next_0 => {
      if (isSwitchingDisabled) {
        return;
      }
      setPreference(next_0);
    };
    $[9] = isSwitchingDisabled;
    $[10] = setPreference;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const setMode = t8;
  let t9;
  if ($[12] !== resolvedMode || $[13] !== setMode) {
    t9 = () => {
      setMode(resolvedMode === "dark" ? "light" : "dark");
    };
    $[12] = resolvedMode;
    $[13] = setMode;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const toggleMode = t9;
  let t10;
  let t11;
  if ($[15] !== preference) {
    t10 = () => {
      if (preference !== "system") {
        return;
      }
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = _temp3;
      mediaQuery.addEventListener("change", handleChange);
      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    };
    t11 = [preference];
    $[15] = preference;
    $[16] = t10;
    $[17] = t11;
  } else {
    t10 = $[16];
    t11 = $[17];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[18] !== activeTheme.mode || $[19] !== activeTheme.tokens.background.main.default || $[20] !== isOutermostProvider) {
    t12 = () => {
      if (!isOutermostProvider || typeof document === "undefined") {
        return;
      }
      document.documentElement.dataset.theme = activeTheme.mode;
      document.documentElement.style.colorScheme = activeTheme.mode;
      document.body.style.backgroundColor = activeTheme.tokens.background.main.default;
    };
    t13 = [activeTheme.mode, activeTheme.tokens.background.main.default, isOutermostProvider];
    $[18] = activeTheme.mode;
    $[19] = activeTheme.tokens.background.main.default;
    $[20] = isOutermostProvider;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  const t14 = isSwitchingDisabled ? noopSetPreference : setPreference;
  const t15 = isSwitchingDisabled ? noopSetMode : setMode;
  const t16 = isSwitchingDisabled ? noopToggleMode : toggleMode;
  let t17;
  if ($[23] !== activeTheme.mode || $[24] !== isThemeSwitchingEnabled || $[25] !== preference || $[26] !== t14 || $[27] !== t15 || $[28] !== t16) {
    t17 = {
      mode: activeTheme.mode,
      preference,
      setPreference: t14,
      setMode: t15,
      toggleMode: t16,
      isThemeSwitchingEnabled
    };
    $[23] = activeTheme.mode;
    $[24] = isThemeSwitchingEnabled;
    $[25] = preference;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const contextValue = t17;
  let t18;
  if ($[30] !== activeTheme || $[31] !== children) {
    t18 = <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>;
    $[30] = activeTheme;
    $[31] = children;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== contextValue || $[34] !== t18) {
    t19 = <ThemeModeContext.Provider value={contextValue}>{t18}</ThemeModeContext.Provider>;
    $[33] = contextValue;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
};
export default AppProvider;
export { useThemeMode } from './useThemeMode';
export { useThemePreference } from './useThemePreference';
export { DEFAULT_THEME_PREFERENCE, DEFAULT_THEME_PREFERENCE_STORAGE_KEY, THEME_PREFERENCES, readStoredThemePreference, resolveThemeMode, type ThemePreference } from './themePreference';
export type { AppProviderProps, ThemeModeContextValue } from './types';
export type { ThemeMode } from '../theme/createPaletteBridge';
export { AppProvider };
function _temp() {
  return DEFAULT_THEME_PREFERENCE;
}
function _temp2() {
  return "light";
}
function _temp3() {
  themePreferenceStore.notify();
}